/**
 * PF Revamp — pure `PfStoreState -> OrderDraftDTO` serializer.
 *
 * Lives next to the store because it walks multiple slices at once. Kept
 * pure so the auto-save subscription can call it from either a React effect
 * or a plain test harness.
 *
 * Inclusion rules (master plan §4 + Round 2 brief):
 *   - INCLUDES: property, cart items, per-item configs, instructions, billing,
 *     draftId, countryCode.
 *   - EXCLUDES: catalogue UI state (search/scroll/active tab) and stepNav
 *     validity flags — session-UI concerns, not order content. The current
 *     step is kept, as `lastStep`, so a resume reopens where the client left.
 *
 * The `updatedAt` field is stamped by `LocalDraftStore.save()` on write, so
 * we emit the current timestamp here as a best-effort default — it's
 * harmless if the store overrides it.
 */

import type {
  BillingPayload,
  DraftAttachmentDTO,
  InstructionsPayload,
  OrderDraftDTO
} from 'models/purchaseFlowRevamp'

import { ProductKind } from 'constants/product/productTypes'

import type { PfStoreState } from './store'
import { PF_VERSION } from './pfVersion'

/**
 * Fast delivery is chosen on Instructions for the order in hand and never saved with a draft: the
 * subscription rule that may withhold it runs on that page, so a resumed draft must not carry it past.
 */
export const withoutFastDelivery = <T extends string>(extraServices: ReadonlyArray<T>): T[] =>
  extraServices.filter((kind) => kind !== ProductKind.FAST_DELIVERY)

/** Pure function used by the auto-save subscription and tests. */
export function serializeDraft(state: PfStoreState): OrderDraftDTO {
  const now = new Date().toISOString()

  const instructions: InstructionsPayload = {
    meeting: state.meeting,
    ctBookingId: state.ctBookingId,
    ctBookingName: state.ctBookingName,
    extraServices: withoutFastDelivery(state.extraServices),
    comments: state.comments,
    scheduledByCT: state.scheduledByCT,
    scheduleMethod: state.scheduleMethod,
  }

  const billing: BillingPayload = {
    invoicingAddress: state.invoicingAddress,
    paymentMethod: state.paymentMethod,
    externalReporting: state.externalReporting,
    reference: state.reference,
    propertyOwner: state.propertyOwner,
    ccEmails: state.ccEmails,
  }

  const isAdminScoped = state.targetUserId !== null || state.targetWorkspaceId !== null

  return {
    schemaVersion: 1,
    pfVersion: PF_VERSION,
    // draftId is authoritatively owned by `draftSlice.draftId`. If not yet
    // assigned, emit an empty string; the adapter writes the canonical id.
    draftId: state.draft.draftId ?? '',
    createdAt: now,
    updatedAt: now,
    initiatedAs: isAdminScoped ? 'ADMIN' : 'CLIENT',
    targetUserId: state.targetUserId ?? undefined,
    workspaceId: state.targetWorkspaceId ?? undefined,
    // The client the admin is ordering FOR. Persisted because the v2
    // create-session body needs it and the admin gate treats a missing email as
    // "no client chosen": without it a resumed admin draft dropped back to the
    // client-selection stage and threw away the workspace it had just restored.
    targetClientEmail: state.targetUserEmail ?? undefined,
    // Kept so a resumed admin order does not quietly start the automation the admin turned off.
    adminSkips: isAdminScoped && (state.adminSkipCtAutomation || state.adminSkipExternalProviderSending)
      ? { ctAutomation: state.adminSkipCtAutomation, externalProviderSending: state.adminSkipExternalProviderSending }
      : undefined,
    property: state.property ?? undefined,
    items: state.items,
    configs: state.productConfigs,
    configsUserConfirmed: state.cartItemUserConfirmed,
    instructions,
    billing,
    countryCode: state.property?.countryCode,
    // The session the uploads live on, and what each line expects to find there.
    // Both are needed to recover files on resume: the session to look them up,
    // the per-line list to know which line owned which file (the listing is keyed
    // by product, which cannot split two lines of the same product).
    session: state.draft.sessionId
      ? { id: state.draft.sessionId, workspaceId: state.draft.sessionWorkspaceId }
      : undefined,
    attachments: serializeAttachments(state.attachments),
    lastStep: state.currentStep,
    uploadDisplayNames: Object.keys(state.uploadDisplayNames).length > 0 ? state.uploadDisplayNames : undefined,
  }
}

/** Drops the signed URLs (short-lived, never worth persisting) and any line with
 *  no files, so an empty map stays absent rather than shipping `{}`. */
function serializeAttachments(
  attachments: PfStoreState['attachments']
): Record<string, DraftAttachmentDTO[]> | undefined {
  const out: Record<string, DraftAttachmentDTO[]> = {}
  for (const [cartItemId, list] of Object.entries(attachments)) {
    if (!list.length) continue
    out[cartItemId] = list.map(({ gcFilename, originalFilename }) => ({ gcFilename, originalFilename }))
  }
  return Object.keys(out).length > 0 ? out : undefined
}
